// General progress dialog with a linear progress bar and a status label.
// Use when you need to wait for a long-running process to complete.

import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Dialog,
    DialogContent,
    DialogTitle,
    FormLabel,
    LinearProgress,
    makeStyles,
    Theme,
    Typography,
} from '@material-ui/core'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
            paddingBottom: theme.spacing(0.25),
        },
    },

    dialogCont: {
        '&.MuiDialogContent-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
        fontSize: pxToRem(18),
    },
    warningDialogCont: {
        '& .MuiDialog-paperScrollPaper': {
            width: pxToRem(540),
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
    title: {
        fontSize: pxToRem(13),
        color: 'rgba(0, 0, 0, 0.54)',
    },
    titleNewProject: {
        fontSize: pxToRem(13),
        color: theme.palette.common.black,
        marginBottom: theme.spacing(3),
    },
    gridItem: {
        paddingTop: theme.spacing(1),
    },
    progressBar: (props: ProgressDialogStyleProps) => ({
        background: '#9AC4E8',
        border: '1px solid #9AC4E8',
        boxSizing: 'border-box',
        borderRadius: '100px',
        height: 10,
        '& .MuiLinearProgress-bar1Indeterminate': {
            background: '#006FC6',
            borderRadius: '100px',
        },
        '& .MuiLinearProgress-bar2Indeterminate': {
            background: '#006FC6',
            borderRadius: '100px',
        },
        marginBottom:
            props.variant === 'New Project'
                ? theme.spacing(5)
                : theme.spacing(8),
    }),
}))
interface ProgressDialogStyleProps {
    /**To detect parent component*/
    variant?: string
}
interface ProgressDialogProps extends ProgressDialogStyleProps {
    /** Unique dialog ID string */
    dialogId: string

    /** Dialog name/title */
    dialogName: string

    /** Whether dialog is open/visible. */
    open: boolean

    /** Percentage at which to show the progress bar. Default is 0. */
    progressPercentage?: number

    /** Description of current status (e.g., "3 of 5 items imported...") */
    statusText?: string

    /** Option to disable title of dialog */
    disableTitle?: boolean
    /** Option to show percentage with dialog title */
    showProgressPercentage?: boolean
}

const ProgressDialog: React.FC<ProgressDialogProps> = ({
    dialogId,
    dialogName,
    open,
    progressPercentage = 0,
    statusText = '',
    variant,
    disableTitle = false,
    showProgressPercentage = false,
}: ProgressDialogProps) => {
    const classes = useStyles({ variant: variant })
    const renderProgressPercentage = React.useCallback(
        () => showProgressPercentage && '(' + progressPercentage + '%)',
        [showProgressPercentage, progressPercentage]
    )
    return (
        <Dialog
            open={open}
            data-testid={dialogId}
            className={classes.warningDialogCont}
            style={{ zIndex: variant === 'New Project' ? 1302 : 1300 }}
            title={disableTitle ? '' : dialogName}
        >
            <DialogTitle
                data-testid="progress-bar-dialog-title"
                className={classes.dialogTitle}
            >
                {dialogName} {renderProgressPercentage()}
            </DialogTitle>
            <DialogContent className={classes.dialogCont}>
                <GridContainer direction="column">
                    <GridItem className={classes.gridItem}>
                        {variant === 'New Project' ? (
                            <Typography className={classes.titleNewProject}>
                                {statusText}
                            </Typography>
                        ) : (
                            <FormLabel className={classes.title}>
                                {statusText}
                            </FormLabel>
                        )}
                        <LinearProgress
                            variant="determinate"
                            value={progressPercentage}
                            className={classes.progressBar}
                            data-testid={`${dialogId}-progress-bar`}
                        />
                    </GridItem>
                </GridContainer>
            </DialogContent>
        </Dialog>
    )
}

export default ProgressDialog
